import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Menu, X, Info, Star, ClipboardList, HardHat, FileCheck2 } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Alexis Construction — Iloilo Homes Builder — concept site. Bold black/red/white
  residential design-build identity (matches their own branding), process-led,
  built around real jobsite and permit-approval photography.
*/

const RED = "#C81E2C";
const BLACK = "#0C0C0C";
const CREAM = "#F7F5F2";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/edwardyapARTnT/";
const NAME = "Alexis Construction";
const SLUG = "alexis-construction-concept";
const P = "/construction/alexis";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function AlexisConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Our Process", "#process"], ["Our Work", "#work"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Barlow Condensed', sans-serif" }} data-testid="alexis-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');
        .ax-body { font-family: 'Inter', sans-serif; }
        #top h1, #top h2, #top h3 { font-family: 'Barlow Condensed', sans-serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4 ax-body">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-black transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.3)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="ax-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded-full object-cover" />
            <span className="text-lg text-white font-bold uppercase tracking-wide">Alexis <span style={{ color: RED }}>Construction</span></span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold uppercase tracking-wide text-white/75 hover:text-white">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="ax-header-cta" className="hidden sm:inline-flex px-5 py-2.5 text-sm font-bold uppercase text-white" style={{ background: RED }}>
            Start Your Project
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="ax-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-black">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white font-semibold uppercase">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="ax-mobile-cta" className="block mt-2 text-center px-5 py-3 text-sm font-bold uppercase text-white" style={{ background: RED }}>Start Your Project</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[82vh] flex items-end" data-testid="ax-hero">
        <img src={`${P}/site-groundbreaking.jpg`} alt="An Alexis Construction jobsite" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(12,12,12,0.1) 0%, rgba(12,12,12,0.6) 60%, rgba(12,12,12,0.95) 100%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Iloilo Homes Builder</span>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white font-extrabold uppercase">Building Dreams. Building Trust.</h1>
            <p className="mt-5 text-lg text-white/85 ax-body font-light leading-relaxed max-w-xl">
              Planning, design and construction tailored to your style, needs and budget — tried and true, from groundbreaking through turnover.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="ax-hero-cta" className="px-7 py-3.5 text-sm font-bold uppercase text-white" style={{ background: RED }}>Start Your Project</a>
              <a href="#work" data-testid="ax-hero-work" className="border border-white/40 px-7 py-3.5 text-sm font-bold uppercase text-white hover:bg-white/10 transition-colors">See Our Work</a>
            </div>
            <p className="mt-7 text-sm text-white/70 ax-body" data-testid="ax-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: RED }} /> 5.0 Google Rating · 5 Reviews · Iloilo City
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>How We Build</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold uppercase leading-snug">Our Process</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [ClipboardList, "Design & Planning", "We plan and design each home around your style, lot and budget before anything breaks ground."],
            [FileCheck2, "Permits & Groundbreaking", "Building permits are processed and approved before we hold a formal groundbreaking on your lot."],
            [HardHat, "Construction", "Our crew manages the build day to day, from foundation through finishing."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: RED }} />
              <h3 className="mt-4 text-xl font-bold uppercase">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed ax-body" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WORK */}
      <section id="work" className="py-20 md:py-28 bg-black">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>On Site</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white font-extrabold uppercase leading-snug">Our Work</h2>
            <p className="mt-4 text-white/70 ax-body leading-relaxed">Public updates from May 2026 show active work across Fatima Jaro, Lapaz, Hacienda Verde, Green Meadows and Palmyra, supported by site teams in Iloilo.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { img: `${P}/site-inspection.jpg`, label: "Reviewing approved plans onsite" },
              { img: `${P}/concrete-pour.jpg`, label: "Structural concrete pour in progress" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="ax-work-item" className="overflow-hidden">
                <img src={it.img} alt={it.label} className="w-full h-80 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/70 ax-body">{it.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-extrabold uppercase leading-snug">Start Your Project</h2>
            <ul className="mt-8 space-y-6 ax-body">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Office</div><div>17U International Corporate Plaza, Megaworld Blvd., Mandurriao, Iloilo City</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+639175341139" className="hover:underline">0917 534 1139</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ax-facebook-link" className="hover:underline">facebook.com/edwardyapARTnT</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="ax"
              accent={RED}
              accentText="#FFFFFF"
              heading="Start Your Project"
              subheading="Tell us about your lot and the home you're picturing — we'll follow up to schedule a consultation."
              projectTypes={["New House Build", "Renovation", "Other"]}
              phone="+639175341139"
              phoneDisplay="0917 534 1139"
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14 ax-body" style={{ background: BLACK, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded-full object-cover" />
              <span className="text-white text-lg font-bold uppercase">Alexis Construction</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Iloilo homes builder — planning, design and construction tailored to your style and budget.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3 uppercase">Contact</div>
            <p className="text-sm">17U International Corporate Plaza,<br />Megaworld Blvd., Mandurriao, Iloilo City</p>
            <p className="text-sm mt-2">0917 534 1139</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-bold uppercase text-white shadow-xl" style={{ background: RED }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
